import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  Activity,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionsService, Subscription } from '../services/subscriptions.service';
import { healthQuestionnaireService } from '../services/health-questionnaire.service';
import { HealthQuestionnaire } from './HealthQuestionnaire';
import { PackageSelection } from './PackageSelection';
import { AddDeviceModal } from './AddDeviceModal';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface PackageManagementProps {
  onBack: () => void;
}

export function PackageManagement({ onBack }: PackageManagementProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showAddDevice, setShowAddDevice] = useState(false);

  const { data: subscription, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: () => subscriptionsService.getCurrentSubscription(),
    enabled: !!user,
    retry: 2,
  });

  const { data: questionnaireCompleted } = useQuery({
    queryKey: ['questionnaire', user?.id],
    queryFn: () => healthQuestionnaireService.isCompleted(),
    enabled: !!user,
    retry: false,
  });

  const handleActivateTrial = async () => {
    try {
      await subscriptionsService.activateFreeTrial();
      toast.success('Free trial activated!');
      await queryClient.invalidateQueries({ queryKey: ['subscription'] });
      refetch();
    } catch (err) {
      toast.error(handleApiError(err));
    }
  };

  const handleQuestionnaireComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['questionnaire'] });
    setShowQuestionnaire(false);
    refetch();
  };

  const handlePackageSelected = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-dropsugar-green animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Error Loading Data</h2>
          <p className="text-gray-600 text-center mb-6">{String(error)}</p>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => refetch()}
              className="flex-1 px-6 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white rounded-xl font-medium"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={onBack}
              className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showQuestionnaire) {
    return (
      <HealthQuestionnaire
        onComplete={handleQuestionnaireComplete}
        onSkip={() => setShowQuestionnaire(false)}
      />
    );
  }

  const sub = subscription as Subscription | null | undefined;
  const pkg = sub?.package;
  const isExpired = !!(sub?.isExpired);
  const hasActiveSubscription =
    !!(sub && (sub.id || sub.status === 'ACTIVE') && !isExpired);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center text-gray-700 hover:text-dropsugar-green mb-6 font-medium transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Your Package</h1>
          <p className="text-gray-600">View your current subscription and make changes</p>
        </div>

        {/* Active subscription banner - always show when we have one */}
        {hasActiveSubscription && (
          <div className="mb-8 p-4 rounded-2xl bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-green-800">Active Subscription</h2>
              <p className="text-green-700 text-sm">
                {sub?.isTrial
                  ? 'Your free trial is active'
                  : pkg
                    ? `You have an active plan: ${pkg.name}`
                    : 'Your subscription is active'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto px-4 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-xl transition-colors"
            >
              Refresh
            </button>
          </div>
        )}

        {sub?.isTrial && (
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-dropsugar-green/30 rounded-3xl p-8 mb-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-8 h-8 text-dropsugar-green" />
                  <h2 className="text-2xl font-bold text-gray-900">Free Trial Active</h2>
                </div>
                <p className="text-gray-700">Explore all features for 7 days, no payment required</p>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-200 text-dropsugar-blue-dark rounded-full">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Trial</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              {sub.trialStartedAt && (
                <div className="p-4 bg-white/70 rounded-xl">
                  <p className="text-sm text-gray-600 mb-1">Trial Started</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(sub.trialStartedAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              )}
              {sub.trialExpiresAt && (
                <div className="p-4 bg-white/70 rounded-xl">
                  <p className="text-sm text-gray-600 mb-1">Trial Expires</p>
                  <p className="text-lg font-semibold text-red-600">
                    {new Date(sub.trialExpiresAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 p-4 bg-dropsugar-green text-white rounded-xl">
              <p className="font-medium mb-1">
                Choose a package before your trial ends to continue enjoying all features!
              </p>
              <p className="text-sm text-blue-100">Select from our packages below to get started.</p>
            </div>
          </div>
        )}

        {sub && pkg && (
          <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Current Package</h2>
                <p className="text-gray-600">Your active subscription details</p>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Active</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl p-8 text-white mb-6">
              <h3 className="text-3xl font-bold mb-2">{pkg.name}</h3>
              <p className="text-blue-100 mb-4">{pkg.description ?? ''}</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">₹{pkg.price?.toLocaleString('en-IN') ?? 0}</span>
                <span className="text-blue-100">
                  for {pkg.durationDays ?? 0} days
                </span>
              </div>
            </div>

            {sub.startedAt && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600 mb-1">Started On</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(sub.startedAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600 mb-1">Duration</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {pkg.durationDays ?? 0} days
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={() => setShowQuestionnaire(true)}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
              >
                Update Health Assessment
              </button>
              <button
                type="button"
                onClick={() => setShowAddDevice(true)}
                className="px-6 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
              >
                <Activity className="w-5 h-5" />
                <span>Add Device</span>
              </button>
            </div>
          </div>
        )}

        {!hasActiveSubscription && (
          <div
            className={`rounded-2xl p-6 mb-8 border-2 ${
              isExpired
                ? 'bg-red-50 border-red-200'
                : 'bg-amber-50 border-amber-200'
            }`}
          >
            {isExpired ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-bold text-red-900">Subscription expired</p>
                    <p className="text-red-800 text-sm">
                      {sub?.expiresAt || sub?.trialExpiresAt
                        ? `Your subscription expired on ${new Date(
                            (sub.expiresAt || sub.trialExpiresAt)!
                          ).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}.`
                        : 'Your subscription has expired.'}{' '}
                      Renew below to continue enjoying all features.
                    </p>
                  </div>
                </div>
                <p className="text-red-700 text-sm font-medium mb-4">
                  Select a package below to renew your subscription.
                </p>
              </>
            ) : (
              <>
                <p className="font-medium text-amber-900 mb-2">No active subscription</p>
                <p className="text-amber-800 text-sm mb-4">
                  Activate a free 7-day trial or choose a package to get started.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-xl font-medium text-sm"
                  >
                    Refresh status
                  </button>
                  <button
                    type="button"
                    onClick={handleActivateTrial}
                    className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium"
                  >
                    Start Free Trial
                  </button>
                </div>
              </>
            )}
            {isExpired && (
              <button
                type="button"
                onClick={() => refetch()}
                className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded-xl font-medium text-sm"
              >
                Refresh status
              </button>
            )}
          </div>
        )}

        <div className="bg-white rounded-3xl shadow-xl p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {isExpired
                ? 'Renew Your Subscription'
                : pkg
                  ? 'Change Package'
                  : 'Select a Package'}
            </h2>
            <p className="text-gray-600">
              {isExpired
                ? 'Choose a package below to renew and continue your health journey'
                : pkg
                  ? 'Choose a different package to better suit your needs'
                  : 'Choose the package that best fits your health goals'}
            </p>
          </div>

          {questionnaireCompleted ? (
            <PackageSelection
              currentSubscription={hasActiveSubscription ? (subscription ?? undefined) : undefined}
              onPackageSelected={handlePackageSelected}
            />
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                <AlertCircle className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Health Assessment Required</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Please complete the health assessment questionnaire to unlock package selection and
                get personalized recommendations.
              </p>
              <button
                type="button"
                onClick={() => setShowQuestionnaire(true)}
                className="px-8 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white rounded-xl font-medium transition-colors"
              >
                Start Health Assessment
              </button>
            </div>
          )}
        </div>
      </div>

      {showAddDevice && (
        <AddDeviceModal onClose={() => setShowAddDevice(false)} />
      )}
    </div>
  );
}
